import type { ComponentProps, ReactNode } from 'react';
import { MenuItem, MenuItems, MenuSeparator } from '@headlessui/react';
import { cn } from '@/lib/cn';

/** Styled Headless UI menu panel, anchored under its button. */
export function MenuPanel({
  children,
  className,
  anchor = 'bottom end',
}: {
  children: ReactNode;
  className?: string;
  anchor?: ComponentProps<typeof MenuItems>['anchor'];
}) {
  return (
    <MenuItems
      anchor={anchor}
      transition
      className={cn(
        'z-40 min-w-56 rounded-control border border-line bg-surface p-1.5 shadow-pop [--anchor-gap:8px] focus:outline-none',
        'origin-top transition duration-150 ease-out data-closed:scale-95 data-closed:opacity-0',
        className,
      )}
    >
      {children}
    </MenuItems>
  );
}

export function MenuEntry({
  icon,
  children,
  hint,
  disabled,
  onClick,
  className,
}: {
  icon?: ReactNode;
  children: ReactNode;
  hint?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
}) {
  return (
    <MenuItem disabled={disabled}>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex w-full items-center gap-3 rounded-[8px] px-3 py-2.5 text-left text-ink',
          'data-focus:bg-ground data-disabled:cursor-not-allowed data-disabled:text-muted',
          className,
        )}
      >
        {icon && <span className="shrink-0 text-ink-2 [&>svg]:size-[18px]">{icon}</span>}
        <span className="min-w-0 flex-1">{children}</span>
        {hint && <span className="shrink-0 text-caption text-muted">{hint}</span>}
      </button>
    </MenuItem>
  );
}

export const MenuDivider = () => <MenuSeparator className="my-1 h-px bg-line" />;
